import React, { useEffect, useMemo, useRef, useState } from 'react';
import { bonusApi, companyApi, employeeApi, projectApi } from '@/lib/api';
import { clampPageToTotal } from '@/lib/pagination';
import { useAuth } from '@/lib/auth';
import { canApproveBonus, canRequestBonus } from '@/lib/roles';
import { DEPARTMENTS, DESIGNATIONS } from '@/lib/employeeOptions';
import { formatCurrency } from '@/lib/payrollCalc';
import { downloadBlobFile } from '@/lib/exportFile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { toast } from 'sonner';
import {
  Search,
  Gift,
  CheckCircle,
  XCircle,
  Loader2,
  Download,
} from 'lucide-react';

const REQUEST_STATUSES = ['Pending', 'Approved', 'Rejected', 'All'];
const PAGE_SIZES = [20, 50, 100];
const MONTHS = [
  ['1', 'January'],
  ['2', 'February'],
  ['3', 'March'],
  ['4', 'April'],
  ['5', 'May'],
  ['6', 'June'],
  ['7', 'July'],
  ['8', 'August'],
  ['9', 'September'],
  ['10', 'October'],
  ['11', 'November'],
  ['12', 'December'],
];

const monthLabel = (value) => MONTHS.find(([month]) => month === String(value))?.[1] || '-';

const currentFinancialYear = () => {
  const now = new Date();
  const year = now.getFullYear();
  const start = now.getMonth() + 1 >= 4 ? year : year - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
};

const defaultRowDraft = () => {
  const now = new Date();
  return {
    bonusType: 'Percentage',
    bonusValue: '',
    bonusTdsType: 'None',
    tdsValue: '',
    remarks: '',
    salaryAsOnMarch: '',
    salaryEdited: false,
    bonusMonth: '3',
    payoutMonth: String(now.getMonth() + 1),
    payoutYear: String(now.getFullYear()),
    byCheque: 0,
    byOther: 0,
    splitTouched: false,
  };
};

const valueOrEmpty = (value) => {
  if (value === '' || value == null) return '';
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  return String(value);
};

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const roundMoney = (value) => Math.round(toNumber(value));

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const isStoredSalaryMissing = (emp) => {
  const stored = emp?.salaryAsOnMarchStored;
  return stored === 0 || stored === '0';
};

const displaySalaryAsOnMarch = (emp) => {
  if (isStoredSalaryMissing(emp)) return emp?.salaryAsOnMarch ?? emp?.gross ?? 0;
  return emp?.salaryAsOnMarch ?? emp?.salary?.salaryAsOnMarch ?? 0;
};

const salaryPayloadValue = (emp, draft) => {
  if (draft?.salaryEdited) return roundMoney(draft.salaryAsOnMarch);
  if (isStoredSalaryMissing(emp)) return 0;
  const stored = emp?.salaryAsOnMarchStored;
  if (stored != null && stored !== '') return roundMoney(stored);
  return roundMoney(draft?.salaryAsOnMarch ?? emp?.salaryAsOnMarch ?? emp?.salary?.salaryAsOnMarch);
};

const draftFromEmployee = (emp) => {
  const now = new Date();
  const pending = emp?.pendingRequest;
  const source = pending || emp?.bonus || {};
  const bonusType = source.bonusType === 'Fixed' ? 'Fixed' : 'Percentage';
  const bonusValue =
    bonusType === 'Fixed'
      ? valueOrEmpty(source.bonusAmount ?? (pending ? source.calculatedAnnualBonus : null))
      : valueOrEmpty(source.bonusPercentage);

  let bonusTdsType = source.bonusTdsType || source.tdsType || 'None';
  if (bonusTdsType !== 'Fixed' && bonusTdsType !== 'Percentage') bonusTdsType = 'None';

  const tdsValue =
    bonusTdsType === 'Fixed'
      ? valueOrEmpty(source.bonusTdsAmount)
      : bonusTdsType === 'Percentage'
        ? valueOrEmpty(source.bonusTdsPercentage)
        : '';

  const net = roundMoney(pending?.netAnnualBonus ?? source?.netAnnualBonus ?? emp?.bonus?.netAnnualBonus ?? 0);
  const savedOther = roundMoney(pending?.byOther ?? source?.byOther ?? 0);
  const savedCheque = pending?.byCheque ?? source?.byCheque;
  const splitTouched =
    savedOther > 0 || (savedCheque != null && savedCheque !== '' && roundMoney(savedCheque) !== net && net > 0);

  return {
    bonusType,
    bonusValue,
    bonusTdsType,
    tdsValue,
    remarks: pending?.remarks || '',
    salaryAsOnMarch: String(displaySalaryAsOnMarch(emp) ?? ''),
    salaryEdited: false,
    bonusMonth: String(source?.bonusMonth || 3),
    payoutMonth: String(source?.payoutMonth || now.getMonth() + 1),
    payoutYear: String(source?.payoutYear || now.getFullYear()),
    byOther: splitTouched ? savedOther : 0,
    byCheque: splitTouched ? roundMoney(savedCheque ?? Math.max(0, net - savedOther)) : net,
    splitTouched,
  };
};

const requestIdOf = (req) => req?.requestId || req?.id || '';

const statusBadgeClass = (status) => {
  if (status === 'Approved') return 'bg-green-100 text-green-700 border-green-200';
  if (status === 'Pending') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  if (status === 'Rejected') return 'bg-red-100 text-red-700 border-red-200';
  return 'bg-muted text-muted-foreground';
};

const normalizeCompanies = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const normalizeList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.employees)) return value.employees;
  return [];
};

const companyIdOf = (company) => company?.companyId || company?.id || '';

const empKey = (emp) => emp?.empId || emp?.id || '';

const employeeDisplayName = (employee) =>
  `${employee?.firstName ?? ''} ${employee?.lastName ?? ''}`.trim() || employee?.name || '';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const serviceHistoryRows = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return ['-'];
  const yearsMatch = text.match(/^(.*?years?)(?:,\s*|\s+)(.+)$/i);
  if (yearsMatch) return [yearsMatch[1].trim(), yearsMatch[2].trim()];
  const commaIndex = text.indexOf(',');
  if (commaIndex > 0) {
    return [text.slice(0, commaIndex).trim(), text.slice(commaIndex + 1).trim()];
  }
  return [text];
};

const buildBonusFieldsFromDraft = (draft) => {
  const fields =
    draft.bonusType === 'Fixed'
      ? {
          bonusType: 'Fixed',
          bonusAmount: toNumber(draft.bonusValue),
        }
      : {
          bonusType: 'Percentage',
          bonusPercentage: toNumber(draft.bonusValue),
        };

  const tdsValue = draft.tdsValue;
  const hasTdsValue = tdsValue !== '' && tdsValue != null;

  if (draft.bonusTdsType === 'Fixed' && hasTdsValue) {
    return {
      ...fields,
      bonusTdsType: 'Fixed',
      bonusTdsAmount: toNumber(tdsValue),
    };
  }

  if (draft.bonusTdsType === 'Percentage' && hasTdsValue) {
    return {
      ...fields,
      bonusTdsType: 'Percentage',
      bonusTdsPercentage: toNumber(tdsValue),
    };
  }

  return {
    ...fields,
    bonusTdsType: 'None',
  };
};

const buildRequestPayloadFromDraft = (emp, draft) => ({
  ...buildBonusFieldsFromDraft(draft),
  salaryAsOnMarch: salaryPayloadValue(emp, draft),
  bonusMonth: toNumber(draft.bonusMonth),
  payoutMonth: toNumber(draft.payoutMonth),
  payoutYear: toNumber(draft.payoutYear),
  byCheque: roundMoney(draft.byCheque),
  byOther: roundMoney(draft.byOther),
  remarks: draft?.remarks?.trim() || '',
});

const splitForNet = (draft, net) => {
  const safeNet = Math.max(0, roundMoney(net));
  if (!draft?.splitTouched) return { byCheque: safeNet, byOther: 0 };
  const byOther = clamp(roundMoney(draft.byOther), 0, safeNet);
  return { byCheque: safeNet - byOther, byOther };
};

const extractPreview = (data) => {
  const nested = data?.bonus && typeof data.bonus === 'object' ? data.bonus : null;
  const source = { ...(data || {}), ...(nested || {}) };
  return {
    netAnnualBonus: roundMoney(
      source.netAnnualBonus ?? source.netBonus ?? source.calculatedNetBonus ?? source.bonusPayable ?? 0
    ),
    calculatedAnnualBonus: source.calculatedAnnualBonus ?? source.annualBonus ?? null,
    calculatedAnnualTds: source.calculatedAnnualTds ?? source.annualBonusTds ?? null,
  };
};

const resolveNet = (emp, draft, preview) => {
  if (preview?.loading) {
    return preview.netAnnualBonus != null ? roundMoney(preview.netAnnualBonus) : null;
  }
  if (preview?.error) return null;
  if (preview && preview.netAnnualBonus != null && toNumber(draft?.bonusValue) > 0) {
    return roundMoney(preview.netAnnualBonus);
  }
  const pendingNet = toNumber(emp?.pendingRequest?.netAnnualBonus);
  if (emp?.pendingRequest && pendingNet > 0) return pendingNet;
  const current = toNumber(emp?.bonus?.netAnnualBonus);
  if (current > 0) return current;
  if (preview && preview.netAnnualBonus != null) return roundMoney(preview.netAnnualBonus);
  return null;
};

const isDraftReady = (draft, preview, net) => {
  if (!draft || preview?.loading || preview?.error) return false;
  if (toNumber(draft.bonusValue) <= 0) return false;
  const bonusMonth = toNumber(draft.bonusMonth);
  const payoutMonth = toNumber(draft.payoutMonth);
  const payoutYear = toNumber(draft.payoutYear);
  if (bonusMonth < 1 || bonusMonth > 12) return false;
  if (payoutMonth < 1 || payoutMonth > 12) return false;
  if (payoutYear < 2000) return false;
  if (net == null || net <= 0) return false;
  return roundMoney(draft.byCheque) + roundMoney(draft.byOther) === roundMoney(net);
};

const CompactSelect = ({ value, onValueChange, options, disabled, testId, className = '' }) => (
  <Select value={value} onValueChange={onValueChange} disabled={disabled}>
    <SelectTrigger className={`h-8 text-xs ${className}`} data-testid={testId}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {options.map((opt) => (
        <SelectItem key={opt.value} value={opt.value} className="text-xs">
          {opt.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const DesignationFilter = ({ value, onValueChange, testId }) => (
  <Select value={value} onValueChange={onValueChange}>
    <SelectTrigger className="w-full sm:w-[180px]" data-testid={testId}>
      <SelectValue placeholder="Designation" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">All Designations</SelectItem>
      {DESIGNATIONS.map((item) => (
        <SelectItem key={item} value={item}>
          {item}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const ProjectFilter = ({ projects, value, onValueChange, testId }) => (
  <Select value={value} onValueChange={onValueChange}>
    <SelectTrigger className="w-full sm:w-[180px]" data-testid={testId}>
      <SelectValue placeholder="Project" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">All Projects</SelectItem>
      {projects.map((project) => (
        <SelectItem key={project.projectId} value={project.projectId}>
          {project.name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const ManagerFilter = ({ managers, value, onValueChange, testId }) => (
  <Select value={value} onValueChange={onValueChange}>
    <SelectTrigger className="w-full sm:w-[220px]" data-testid={testId}>
      <SelectValue placeholder="Reporting Manager" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">All Managers</SelectItem>
      {managers.map((manager) => (
        <SelectItem key={manager.id} value={manager.id}>
          {manager.name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const MonthFilter = ({ value, onValueChange, testId, allLabel }) => (
  <Select value={value} onValueChange={onValueChange}>
    <SelectTrigger className="w-full sm:w-[160px]" data-testid={testId}>
      <SelectValue placeholder={allLabel} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{allLabel}</SelectItem>
      {MONTHS.map(([month, label]) => (
        <SelectItem key={month} value={month}>
          {label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const BonusPage = () => {
  const { user } = useAuth();
  const canRequest = canRequestBonus(user?.role);
  const canApprove = canApproveBonus(user?.role);

  const [mainTab, setMainTab] = useState('employees');
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [managers, setManagers] = useState([]);

  const [employees, setEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [debouncedEmpSearch, setDebouncedEmpSearch] = useState('');
  const [department, setDepartment] = useState('all');
  const [company, setCompany] = useState('all');
  const [designation, setDesignation] = useState('all');
  const [project, setProject] = useState('all');
  const [reportingManager, setReportingManager] = useState('all');
  const [empPage, setEmpPage] = useState(1);
  const [empPageSize, setEmpPageSize] = useState(20);
  const [selectedEmpIds, setSelectedEmpIds] = useState([]);
  const [rowDrafts, setRowDrafts] = useState({});
  const [previews, setPreviews] = useState({});
  const [autoApprove, setAutoApprove] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [requests, setRequests] = useState([]);
  const [requestStatus, setRequestStatus] = useState('Pending');
  const [reqSearch, setReqSearch] = useState('');
  const [debouncedReqSearch, setDebouncedReqSearch] = useState('');
  const [reqDepartment, setReqDepartment] = useState('all');
  const [reqDesignation, setReqDesignation] = useState('all');
  const [reqProject, setReqProject] = useState('all');
  const [reqManager, setReqManager] = useState('all');
  const [reqBonusMonth, setReqBonusMonth] = useState('all');
  const [reqPayoutMonth, setReqPayoutMonth] = useState('all');
  const [reqPayoutYear, setReqPayoutYear] = useState('');
  const [reqPage, setReqPage] = useState(1);
  const [reqPageSize, setReqPageSize] = useState(20);
  const [reqTotal, setReqTotal] = useState(0);
  const [reqPages, setReqPages] = useState(1);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, all: 0 });
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [financialYear, setFinancialYear] = useState(currentFinancialYear);

  const rowDraftsRef = useRef({});
  const employeesRef = useRef([]);
  const previewTimersRef = useRef({});
  const previewSeqRef = useRef({});
  const mountedRef = useRef(true);
  const empFetchSeqRef = useRef(0);
  const reqFetchSeqRef = useRef(0);

  useEffect(() => {
    rowDraftsRef.current = rowDrafts;
  }, [rowDrafts]);

  useEffect(() => {
    employeesRef.current = employees;
  }, [employees]);

  useEffect(() => {
    mountedRef.current = true;
    const timers = previewTimersRef.current;
    return () => {
      mountedRef.current = false;
      Object.values(timers).forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const companyNameById = useMemo(() => {
    const map = {};
    companies.forEach((item) => {
      const id = companyIdOf(item);
      if (id) map[id] = item?.name || id;
    });
    return map;
  }, [companies]);

  const projectOptions = useMemo(
    () => (projects || []).filter((item) => item?.projectId && item?.name),
    [projects]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedEmpSearch(empSearch), 350);
    return () => window.clearTimeout(timer);
  }, [empSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedReqSearch(reqSearch), 350);
    return () => window.clearTimeout(timer);
  }, [reqSearch]);

  useEffect(() => {
    setEmpPage(1);
    setSelectedEmpIds([]);
  }, [debouncedEmpSearch, department, company, designation, project, reportingManager]);

  useEffect(() => {
    setReqPage(1);
    setSelectedRequestIds([]);
  }, [
    debouncedReqSearch,
    requestStatus,
    reqDepartment,
    reqDesignation,
    reqProject,
    reqManager,
    reqBonusMonth,
    reqPayoutMonth,
    reqPayoutYear,
  ]);

  useEffect(() => {
    let cancelled = false;
    companyApi
      .getAll()
      .then((data) => {
        if (!cancelled) setCompanies(normalizeCompanies(data));
      })
      .catch(() => {
        if (!cancelled) setCompanies([]);
      });
    projectApi
      .getAll()
      .then((data) => {
        if (!cancelled) setProjects(normalizeList(data));
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      });
    employeeApi
      .getAll({ status: 'Active', sortBy: 'name' })
      .then((data) => {
        if (cancelled) return;
        const options = normalizeList(data)
          .map((person) => {
            const id = person?.empId || '';
            const name = employeeDisplayName(person);
            if (!id || !name) return null;
            return { id: String(id), name };
          })
          .filter(Boolean);
        setManagers(options);
      })
      .catch(() => {
        if (!cancelled) setManagers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchEmployees = async () => {
    const seq = empFetchSeqRef.current + 1;
    empFetchSeqRef.current = seq;
    setTableLoading(true);
    try {
      const params = { status: 'Active' };
      if (debouncedEmpSearch?.trim()) params.search = debouncedEmpSearch.trim();
      if (department && department !== 'all') params.department = department;
      if (designation && designation !== 'all') params.designation = designation;
      if (company && company !== 'all') params.company = company;
      if (project && project !== 'all') params.project = project;
      if (reportingManager && reportingManager !== 'all') params.reportingManager = reportingManager;
      const data = await bonusApi.getEmployees(params);
      const items = Array.isArray(data?.items) ? data.items : [];
      if (!mountedRef.current || empFetchSeqRef.current !== seq) return;
      setEmployees(items);
      setPreviews({});
      setRowDrafts(() => {
        const next = {};
        items.forEach((emp) => {
          const id = empKey(emp);
          if (!id) return;
          next[id] = draftFromEmployee(emp);
        });
        return next;
      });
    } catch (err) {
      if (empFetchSeqRef.current !== seq) return;
      toast.error(err?.message || 'Failed to load employees');
      if (mountedRef.current) setEmployees([]);
    } finally {
      if (mountedRef.current && empFetchSeqRef.current === seq) {
        setLoading(false);
        setTableLoading(false);
      }
    }
  };

  const fetchRequests = async () => {
    const seq = reqFetchSeqRef.current + 1;
    reqFetchSeqRef.current = seq;
    setTableLoading(true);
    try {
      const params = {
        status: requestStatus,
        page: reqPage,
        limit: reqPageSize,
      };
      if (debouncedReqSearch?.trim()) params.search = debouncedReqSearch.trim();
      if (reqDepartment && reqDepartment !== 'all') params.department = reqDepartment;
      if (reqDesignation && reqDesignation !== 'all') params.designation = reqDesignation;
      if (reqProject && reqProject !== 'all') params.project = reqProject;
      if (reqManager && reqManager !== 'all') params.reportingManager = reqManager;
      if (reqBonusMonth && reqBonusMonth !== 'all') params.bonusMonth = reqBonusMonth;
      if (reqPayoutMonth && reqPayoutMonth !== 'all') params.payoutMonth = reqPayoutMonth;
      if (reqPayoutYear?.trim()) params.payoutYear = reqPayoutYear.trim();
      const data = await bonusApi.getRequests(params);
      if (!mountedRef.current || reqFetchSeqRef.current !== seq) return;
      setRequests(Array.isArray(data?.items) ? data.items : []);
      setReqTotal(data?.total ?? 0);
      setReqPages(data?.pages ?? 1);
      setReqPage((page) => clampPageToTotal(page, data?.pages ?? 1));
      if (data?.counts) {
        setCounts({
          pending: data.counts.pending ?? 0,
          approved: data.counts.approved ?? 0,
          rejected: data.counts.rejected ?? 0,
          all: data.counts.all ?? 0,
        });
      }
    } catch (err) {
      if (reqFetchSeqRef.current !== seq) return;
      toast.error(err?.message || 'Failed to load bonus requests');
      if (mountedRef.current) setRequests([]);
    } finally {
      if (mountedRef.current && reqFetchSeqRef.current === seq) {
        setLoading(false);
        setTableLoading(false);
      }
    }
  };

  useEffect(() => {
    if (mainTab === 'employees') fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab, debouncedEmpSearch, department, company, designation, project, reportingManager]);

  useEffect(() => {
    if (mainTab === 'requests') fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mainTab,
    requestStatus,
    reqPage,
    reqPageSize,
    debouncedReqSearch,
    reqDepartment,
    reqDesignation,
    reqProject,
    reqManager,
    reqBonusMonth,
    reqPayoutMonth,
    reqPayoutYear,
  ]);

  useEffect(() => {
    if (mainTab !== 'employees') return undefined;
    let cancelled = false;
    const loadCounts = async () => {
      try {
        const data = await bonusApi.getRequests({ status: 'Pending', page: 1, limit: 1 });
        if (cancelled || !data?.counts) return;
        setCounts({
          pending: data.counts.pending ?? 0,
          approved: data.counts.approved ?? 0,
          rejected: data.counts.rejected ?? 0,
          all: data.counts.all ?? 0,
        });
      } catch {
        /* ignore */
      }
    };
    loadCounts();
    return () => {
      cancelled = true;
    };
  }, [mainTab, employees]);

  const runPreviewRef = useRef(async () => {});
  runPreviewRef.current = async (id) => {
    const emp = employeesRef.current.find((item) => empKey(item) === id);
    const draft = rowDraftsRef.current[id];
    if (!emp || !draft || toNumber(draft.bonusValue) <= 0) {
      if (!mountedRef.current) return;
      setPreviews((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }

    const seq = (previewSeqRef.current[id] || 0) + 1;
    previewSeqRef.current[id] = seq;
    const previewBody = {
      employeeId: id,
      ...buildBonusFieldsFromDraft(draft),
      salaryAsOnMarch: salaryPayloadValue(emp, draft),
      bonusMonth: toNumber(draft.bonusMonth),
      payoutMonth: toNumber(draft.payoutMonth),
      payoutYear: toNumber(draft.payoutYear),
    };
    const requestKey = JSON.stringify(previewBody);
    try {
      const data = await bonusApi.preview(previewBody);
      if (!mountedRef.current || previewSeqRef.current[id] !== seq) return;
      const currentEmp = employeesRef.current.find((item) => empKey(item) === id);
      const currentDraft = rowDraftsRef.current[id];
      if (!currentEmp || !currentDraft) return;
      const currentKey = JSON.stringify({
        employeeId: id,
        ...buildBonusFieldsFromDraft(currentDraft),
        salaryAsOnMarch: salaryPayloadValue(currentEmp, currentDraft),
        bonusMonth: toNumber(currentDraft.bonusMonth),
        payoutMonth: toNumber(currentDraft.payoutMonth),
        payoutYear: toNumber(currentDraft.payoutYear),
      });
      if (currentKey !== requestKey) return;
      const parsed = extractPreview(data);
      setPreviews((prev) => ({
        ...prev,
        [id]: { ...parsed, loading: false, error: null },
      }));
      setRowDrafts((prev) => {
        const current = prev[id];
        if (!current) return prev;
        const nextSplit = splitForNet(current, parsed.netAnnualBonus);
        if (
          roundMoney(current.byCheque) === nextSplit.byCheque &&
          roundMoney(current.byOther) === nextSplit.byOther
        ) {
          return prev;
        }
        return { ...prev, [id]: { ...current, ...nextSplit } };
      });
    } catch (err) {
      if (!mountedRef.current || previewSeqRef.current[id] !== seq) return;
      setPreviews((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), loading: false, error: err?.message || 'Preview failed' },
      }));
    }
  };

  const schedulePreview = (id) => {
    setPreviews((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), loading: true, error: null },
    }));
    window.clearTimeout(previewTimersRef.current[id]);
    previewTimersRef.current[id] = window.setTimeout(() => {
      runPreviewRef.current(id);
    }, 400);
  };

  const empTotalPages = Math.max(1, Math.ceil(employees.length / empPageSize));
  const pagedEmployees = useMemo(() => {
    const start = (empPage - 1) * empPageSize;
    return employees.slice(start, start + empPageSize);
  }, [employees, empPage, empPageSize]);

  useEffect(() => {
    setEmpPage((page) => clampPageToTotal(page, empTotalPages));
  }, [empTotalPages]);

  const selectableEmployees = useMemo(
    () => employees.filter((emp) => !emp?.bonus?.hasPendingRequest),
    [employees]
  );

  const allPageSelectableIds = useMemo(
    () =>
      pagedEmployees
        .filter((emp) => !emp?.bonus?.hasPendingRequest)
        .map((emp) => empKey(emp))
        .filter(Boolean),
    [pagedEmployees]
  );

  const allPageSelected =
    allPageSelectableIds.length > 0 && allPageSelectableIds.every((id) => selectedEmpIds.includes(id));

  const handleToggleEmp = (id, checked) => {
    if (!id) return;
    setSelectedEmpIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((item) => item !== id);
    });
  };

  const handleToggleAllPage = (checked) => {
    setSelectedEmpIds((prev) => {
      if (checked) {
        const set = new Set(prev);
        allPageSelectableIds.forEach((id) => set.add(id));
        return [...set];
      }
      return prev.filter((id) => !allPageSelectableIds.includes(id));
    });
  };

  const updateDraft = (id, patch) => {
    setRowDrafts((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || defaultRowDraft()),
        ...patch,
      },
    }));
  };

  const handleChequeChange = (id, net, raw) => {
    if (net == null) return;
    const cheque = raw === '' ? 0 : clamp(roundMoney(raw), 0, net);
    updateDraft(id, {
      byCheque: cheque,
      byOther: roundMoney(net) - cheque,
      splitTouched: true,
    });
  };

  const handleCashChange = (id, net, raw) => {
    if (net == null) return;
    const byOther = raw === '' ? 0 : clamp(roundMoney(raw), 0, net);
    updateDraft(id, {
      byCheque: roundMoney(net) - byOther,
      byOther,
      splitTouched: true,
    });
  };

  const handleSubmitSelected = async () => {
    const targets = employees.filter((emp) => {
      const id = empKey(emp);
      return selectedEmpIds.includes(id) && !emp?.bonus?.hasPendingRequest;
    });

    if (!targets.length) {
      toast.error('Select at least one eligible employee');
      return;
    }

    const calculating = targets.filter((emp) => previews[empKey(emp)]?.loading);
    if (calculating.length) {
      toast.error(`Bonus is still calculating for ${calculating.length} employee(s)`);
      return;
    }

    const invalid = targets.filter((emp) => {
      const id = empKey(emp);
      const draft = rowDrafts[id];
      return !isDraftReady(draft, previews[id], resolveNet(emp, draft, previews[id]));
    });
    if (invalid.length) {
      toast.error(`Complete bonus details for ${invalid.length} selected employee(s)`);
      return;
    }

    setSubmitting(true);
    try {
      const groups = new Map();
      targets.forEach((emp) => {
        const id = empKey(emp);
        const payload = buildRequestPayloadFromDraft(emp, rowDrafts[id]);
        const key = JSON.stringify(payload);
        if (!groups.has(key)) {
          groups.set(key, { payload, employeeIds: [] });
        }
        groups.get(key).employeeIds.push(id);
      });

      for (const group of groups.values()) {
        await bonusApi.request({
          employeeIds: group.employeeIds,
          ...group.payload,
          autoApprove: canApprove ? !!autoApprove : false,
        });
      }

      toast.success(
        targets.length === 1
          ? 'Bonus request submitted'
          : `Bonus request submitted for ${targets.length} employees`
      );
      setSelectedEmpIds([]);
      setAutoApprove(false);
      await fetchEmployees();
    } catch (err) {
      toast.error(err?.message || 'Failed to submit bonus request');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingSelectableIds = useMemo(
    () =>
      requests
        .filter((req) => req?.status === 'Pending')
        .map((req) => requestIdOf(req))
        .filter(Boolean),
    [requests]
  );

  const allPendingSelected =
    pendingSelectableIds.length > 0 && pendingSelectableIds.every((id) => selectedRequestIds.includes(id));

  const handleToggleRequest = (id, checked) => {
    if (!id) return;
    setSelectedRequestIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((item) => item !== id);
    });
  };

  const handleToggleAllPending = (checked) => {
    setSelectedRequestIds((prev) => {
      if (checked) {
        const set = new Set(prev);
        pendingSelectableIds.forEach((id) => set.add(id));
        return [...set];
      }
      return prev.filter((id) => !pendingSelectableIds.includes(id));
    });
  };

  const runConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, ids } = confirmAction;
    setActionLoading(true);
    try {
      if (type === 'approve-one') {
        await bonusApi.approve(ids[0]);
        toast.success('Bonus request approved');
      } else if (type === 'reject-one') {
        await bonusApi.reject(ids[0], {});
        toast.success('Bonus request rejected');
      } else if (type === 'approve-bulk') {
        await bonusApi.bulkAction({ requestIds: ids, action: 'approve' });
        toast.success(`Approved ${ids.length} request(s)`);
      } else if (type === 'reject-bulk') {
        await bonusApi.bulkAction({ requestIds: ids, action: 'reject' });
        toast.success(`Rejected ${ids.length} request(s)`);
      }
      setSelectedRequestIds([]);
      setConfirmAction(null);
      await fetchRequests();
      if (mainTab === 'employees') await fetchEmployees();
    } catch (err) {
      toast.error(err?.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = {};
      if (financialYear?.trim()) params.financialYear = financialYear.trim();
      if (mainTab === 'requests') {
        params.status = requestStatus;
        if (reqDepartment !== 'all') params.department = reqDepartment;
        if (reqDesignation !== 'all') params.designation = reqDesignation;
        if (reqProject !== 'all') params.project = reqProject;
        if (reqManager !== 'all') params.reportingManager = reqManager;
        if (reqBonusMonth !== 'all') params.bonusMonth = reqBonusMonth;
        if (reqPayoutMonth !== 'all') params.payoutMonth = reqPayoutMonth;
        if (reqPayoutYear?.trim()) params.payoutYear = reqPayoutYear.trim();
      } else {
        params.status = 'Approved';
        if (department !== 'all') params.department = department;
        if (designation !== 'all') params.designation = designation;
        if (company !== 'all') params.company = company;
        if (project !== 'all') params.project = project;
        if (reportingManager !== 'all') params.reportingManager = reportingManager;
      }
      const response = await bonusApi.export(params);
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const matchedFileName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
      const fileName = matchedFileName || `bonus_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadBlobFile(blob, fileName);
      toast.success('Bonus export downloaded');
    } catch (err) {
      toast.error(err?.message || 'Failed to export bonus');
    } finally {
      setExporting(false);
    }
  };

  const confirmTitle = useMemo(() => {
    if (!confirmAction) return '';
    if (confirmAction.type === 'approve-one' || confirmAction.type === 'approve-bulk') {
      return confirmAction.ids.length > 1
        ? `Approve ${confirmAction.ids.length} bonus requests?`
        : 'Approve bonus request?';
    }
    return confirmAction.ids.length > 1
      ? `Reject ${confirmAction.ids.length} bonus requests?`
      : 'Reject bonus request?';
  }, [confirmAction]);

  const confirmDescription = useMemo(() => {
    if (!confirmAction) return '';
    if (confirmAction.type?.startsWith('approve')) {
      return 'This will update the employee salary structure and recalculate CTC.';
    }
    return 'The request will be marked as rejected. You can submit a new request later.';
  }, [confirmAction]);

  if (loading && employees.length === 0 && requests.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]" data-testid="bonus-page-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#0047AB]" />
      </div>
    );
  }

  const empColSpan = canRequest ? 17 : 10;
  const requestColSpan = 10 + (canApprove && requestStatus === 'Pending' ? 1 : 0) + (canApprove ? 1 : 0);

  return (
    <div data-testid="bonus-page" className="space-y-3 animate-fadeIn">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-[Manrope]">Bonus</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Request and manage annual employee bonuses
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            value={financialYear}
            onChange={(event) => setFinancialYear(event.target.value)}
            placeholder="2025-26"
            aria-label="Financial year"
            className="h-10 w-full sm:w-[7.5rem]"
            data-testid="bonus-financial-year"
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
            data-testid="bonus-export-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
          {canRequest && mainTab === 'employees' && (
            <>
              {canApprove && (
                <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-background">
                  <Switch
                    checked={autoApprove}
                    onCheckedChange={setAutoApprove}
                    data-testid="bonus-auto-approve"
                  />
                  <Label className="text-xs whitespace-nowrap">Auto approve</Label>
                </div>
              )}
              <Button
                onClick={handleSubmitSelected}
                disabled={selectedEmpIds.length === 0 || submitting}
                className="bg-[#0047AB] hover:bg-[#003d96] text-white"
                data-testid="request-bonus-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Gift className="w-4 h-4 mr-2" />
                )}
                Request Bonus{selectedEmpIds.length > 0 ? ` (${selectedEmpIds.length})` : ''}
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs
        value={mainTab}
        onValueChange={(value) => {
          setMainTab(value);
          setSelectedEmpIds([]);
          setSelectedRequestIds([]);
        }}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="requests" data-testid="tab-bonus-requests" className="gap-2">
            Requested Bonus
            {counts.pending > 0 && (
              <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 text-xs leading-none px-1.5 py-0.5">
                {counts.pending}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-bonus-employees">
            All Employees
          </TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="space-y-4">
          <Card className="border border-border shadow-sm">
            <CardHeader className="py-3 space-y-3">
              <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                <div className="relative w-full sm:max-w-sm">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="bonus-employees-search"
                    placeholder="Search by name or ID..."
                    value={empSearch}
                    onChange={(event) => setEmpSearch(event.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={department} onValueChange={setDepartment}>
                  <SelectTrigger className="w-full sm:w-[200px]" data-testid="bonus-department-filter">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {DEPARTMENTS.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={company} onValueChange={setCompany}>
                  <SelectTrigger className="w-full sm:w-[220px]" data-testid="bonus-company-filter">
                    <SelectValue placeholder="Company" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {companies
                      .filter((item) => companyIdOf(item) && item?.name)
                      .map((item) => (
                        <SelectItem key={companyIdOf(item)} value={companyIdOf(item)}>
                          {item.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <DesignationFilter
                  value={designation}
                  onValueChange={setDesignation}
                  testId="bonus-designation-filter"
                />
                <ProjectFilter
                  projects={projectOptions}
                  value={project}
                  onValueChange={setProject}
                  testId="bonus-project-filter"
                />
                <ManagerFilter
                  managers={managers}
                  value={reportingManager}
                  onValueChange={setReportingManager}
                  testId="bonus-manager-filter"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    {canRequest && (
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allPageSelected}
                          onCheckedChange={(value) => handleToggleAllPage(!!value)}
                          aria-label="Select all on page"
                          data-testid="bonus-emp-select-all"
                        />
                      </TableHead>
                    )}
                    <TableHead className="text-xs uppercase tracking-widest min-w-[140px]">Employee</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Dept</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest min-w-[120px]">Date of Joining</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest min-w-[160px]">Service History</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Working Days</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest min-w-[140px]">Salary as on March</TableHead>
                    {canRequest ? (
                      <>
                        <TableHead className="text-xs uppercase tracking-widest min-w-[120px]">Bonus Type</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest min-w-[110px]">Bonus % / Amt</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest min-w-[110px]">TDS Type</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest min-w-[110px]">TDS % / Amt</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest min-w-[130px]">Payout Month</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest min-w-[100px]">Payout Year</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-right min-w-[110px]">Net Bonus</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest min-w-[110px]">By Cheque</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest min-w-[110px]">By Cash</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest min-w-[140px]">Remarks</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead className="text-xs uppercase tracking-widest text-right">Net Bonus</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-right">TDS</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-right">By Cheque</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-right">By Cash</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableLoading && (
                    <TableRow>
                      <TableCell colSpan={empColSpan} className="text-center py-8 text-muted-foreground">
                        Loading employees...
                      </TableCell>
                    </TableRow>
                  )}
                  {!tableLoading && pagedEmployees.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={empColSpan} className="text-center py-8 text-muted-foreground">
                        No employees found
                      </TableCell>
                    </TableRow>
                  )}
                  {!tableLoading &&
                    pagedEmployees.map((emp) => {
                      const id = empKey(emp);
                      const pending = !!emp?.bonus?.hasPendingRequest;
                      const draft = rowDrafts[id] || defaultRowDraft();
                      const preview = previews[id];
                      const rowLocked = pending || !canRequest;
                      const isSelected = selectedEmpIds.includes(id);
                      const net = resolveNet(emp, draft, preview);
                      const salaryFallback = !draft.salaryEdited && isStoredSalaryMissing(emp);
                      const monthOptions = MONTHS.map(([month, label]) => ({ value: month, label }));

                      const handleRowClick = (event) => {
                        if (!canRequest || pending) return;
                        const target = event?.target;
                        if (!(target instanceof Element)) return;
                        if (
                          target.closest(
                            'button, input, textarea, select, a, label, [role="checkbox"], [role="combobox"], [role="option"], [data-radix-collection-item]'
                          )
                        ) {
                          return;
                        }
                        handleToggleEmp(id, !isSelected);
                      };

                      return (
                        <TableRow
                          key={id}
                          data-testid={`bonus-emp-row-${id}`}
                          onClick={handleRowClick}
                          className={
                            canRequest && !pending
                              ? `cursor-pointer ${isSelected ? 'bg-[#0047AB]/5' : 'hover:bg-muted/40'}`
                              : undefined
                          }
                        >
                          {canRequest && (
                            <TableCell>
                              <Checkbox
                                checked={isSelected}
                                disabled={pending}
                                onCheckedChange={(value) => handleToggleEmp(id, !!value)}
                                aria-label={`Select ${emp.name || id}`}
                                data-testid={`bonus-emp-check-${id}`}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">
                                {emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim()}
                              </p>
                              <p className="text-xs text-muted-foreground font-mono">{id}</p>
                              <p className="text-xs text-muted-foreground">
                                {companyNameById[emp.company] || emp.company || ''}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{emp.department || '-'}</TableCell>
                          <TableCell className="text-sm whitespace-nowrap">{formatDate(emp.joiningDate)}</TableCell>
                          <TableCell className="text-sm leading-tight">
                            {serviceHistoryRows(emp.serviceHistory).map((row) => (
                              <p key={row} className="whitespace-nowrap">
                                {row}
                              </p>
                            ))}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {emp.workingDays == null || emp.workingDays === '' ? '-' : emp.workingDays}
                          </TableCell>
                          <TableCell>
                            <div className={salaryFallback ? 'text-muted-foreground italic' : ''}>
                              <p
                                className="text-right font-mono text-sm"
                                data-testid={`bonus-salary-march-${id}`}
                              >
                                {formatCurrency(displaySalaryAsOnMarch(emp))}
                              </p>
                              {salaryFallback && (
                                <p className="text-[10px] italic text-muted-foreground text-right">Using current gross</p>
                              )}
                            </div>
                          </TableCell>

                          {canRequest ? (
                            <>
                              <TableCell>
                                <CompactSelect
                                  value={draft.bonusType}
                                  disabled={rowLocked}
                                  onValueChange={(value) => {
                                    updateDraft(id, { bonusType: value });
                                    schedulePreview(id);
                                  }}
                                  testId={`bonus-type-${id}`}
                                  className="w-[108px]"
                                  options={[
                                    { value: 'Percentage', label: 'Percentage' },
                                    { value: 'Fixed', label: 'Fixed' },
                                  ]}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  disabled={rowLocked}
                                  className="h-8 w-[110px] text-xs font-mono"
                                  placeholder={draft.bonusType === 'Fixed' ? 'Annual amt' : '%'}
                                  value={draft.bonusValue}
                                  onChange={(event) => {
                                    updateDraft(id, { bonusValue: event.target.value });
                                    schedulePreview(id);
                                  }}
                                  data-testid={`bonus-value-${id}`}
                                />
                              </TableCell>
                              <TableCell>
                                <CompactSelect
                                  value={draft.bonusTdsType}
                                  disabled={rowLocked}
                                  onValueChange={(value) => {
                                    updateDraft(id, { bonusTdsType: value });
                                    schedulePreview(id);
                                  }}
                                  testId={`bonus-tds-type-${id}`}
                                  className="w-[108px]"
                                  options={[
                                    { value: 'None', label: 'None' },
                                    { value: 'Percentage', label: 'Percentage' },
                                    { value: 'Fixed', label: 'Fixed' },
                                  ]}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  disabled={rowLocked || draft.bonusTdsType === 'None'}
                                  className="h-8 w-[110px] text-xs font-mono"
                                  placeholder={
                                    draft.bonusTdsType === 'Fixed'
                                      ? 'Annual TDS'
                                      : draft.bonusTdsType === 'None'
                                        ? '-'
                                        : '%'
                                  }
                                  value={draft.bonusTdsType === 'None' ? '' : draft.tdsValue}
                                  onChange={(event) => {
                                    updateDraft(id, { tdsValue: event.target.value });
                                    schedulePreview(id);
                                  }}
                                  data-testid={`bonus-tds-value-${id}`}
                                />
                              </TableCell>
                              <TableCell>
                                <CompactSelect
                                  value={String(draft.payoutMonth || '1')}
                                  disabled={rowLocked}
                                  onValueChange={(value) => {
                                    updateDraft(id, { payoutMonth: value });
                                    schedulePreview(id);
                                  }}
                                  testId={`bonus-payout-month-${id}`}
                                  className="w-[124px]"
                                  options={monthOptions}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="2000"
                                  disabled={rowLocked}
                                  className="h-8 w-[90px] text-xs font-mono"
                                  value={draft.payoutYear}
                                  onChange={(event) => {
                                    updateDraft(id, { payoutYear: event.target.value });
                                    schedulePreview(id);
                                  }}
                                  data-testid={`bonus-payout-year-${id}`}
                                />
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm font-medium">
                                <div className="inline-flex items-center justify-end gap-1">
                                  {preview?.loading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                                  <span>{net != null ? formatCurrency(net) : '-'}</span>
                                </div>
                                {preview?.error && (
                                  <p className="text-[10px] text-red-600 font-sans font-normal">Recalc failed</p>
                                )}
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  disabled={rowLocked || net == null}
                                  className="h-8 w-[110px] text-xs font-mono"
                                  value={draft.byCheque}
                                  onChange={(event) => handleChequeChange(id, net, event.target.value)}
                                  data-testid={`bonus-cheque-${id}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  disabled={rowLocked || net == null}
                                  className="h-8 w-[110px] text-xs font-mono"
                                  value={draft.byOther}
                                  onChange={(event) => handleCashChange(id, net, event.target.value)}
                                  data-testid={`bonus-cash-${id}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  disabled={rowLocked}
                                  className="h-8 w-[140px] text-xs"
                                  placeholder="Optional"
                                  value={draft.remarks || ''}
                                  onChange={(event) => updateDraft(id, { remarks: event.target.value })}
                                  data-testid={`bonus-remarks-${id}`}
                                />
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell className="text-right font-mono text-sm">
                                {formatCurrency(emp?.bonus?.netAnnualBonus)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {formatCurrency(emp?.bonus?.annualBonusTds)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {formatCurrency(emp?.bonus?.byCheque ?? emp?.byCheque)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {formatCurrency(emp?.bonus?.byOther ?? emp?.byOther)}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {employees.length} employee{employees.length === 1 ? '' : 's'}
              {selectableEmployees.length < employees.length
                ? ` · ${employees.length - selectableEmployees.length} pending`
                : ''}
            </p>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <Select
                value={String(empPageSize)}
                onValueChange={(value) => {
                  setEmpPageSize(Number(value));
                  setEmpPage(1);
                }}
              >
                <SelectTrigger className="w-[100px]" data-testid="bonus-emp-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size} / page
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={empPage <= 1}
                onClick={() => setEmpPage((page) => Math.max(1, page - 1))}
              >
                Prev
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {empPage} / {empTotalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={empPage >= empTotalPages}
                onClick={() => setEmpPage((page) => Math.min(empTotalPages, page + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="requests" className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {REQUEST_STATUSES.map((status) => {
              const key = status.toLowerCase();
              const count = counts[key] ?? 0;
              return (
                <Button
                  key={status}
                  size="sm"
                  variant={requestStatus === status ? 'default' : 'outline'}
                  className={requestStatus === status ? 'bg-[#0047AB] hover:bg-[#003d96] text-white' : ''}
                  onClick={() => setRequestStatus(status)}
                  data-testid={`bonus-status-${status.toLowerCase()}`}
                >
                  {status}
                  <Badge
                    className={`ml-2 text-xs ${
                      requestStatus === status
                        ? 'bg-white/20 text-white border-white/30'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {count}
                  </Badge>
                </Button>
              );
            })}
          </div>

          <Card className="border border-border shadow-sm">
            <CardHeader className="py-3">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                  <div className="relative w-full sm:max-w-sm">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="bonus-requests-search"
                      placeholder="Search name, ID, or company..."
                      value={reqSearch}
                      onChange={(event) => setReqSearch(event.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={reqDepartment} onValueChange={setReqDepartment}>
                    <SelectTrigger className="w-full sm:w-[200px]" data-testid="bonus-req-department-filter">
                      <SelectValue placeholder="Department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {DEPARTMENTS.map((item) => (
                        <SelectItem key={item} value={item}>
                          {item}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <DesignationFilter
                    value={reqDesignation}
                    onValueChange={setReqDesignation}
                    testId="bonus-req-designation-filter"
                  />
                  <ProjectFilter
                    projects={projectOptions}
                    value={reqProject}
                    onValueChange={setReqProject}
                    testId="bonus-req-project-filter"
                  />
                  <ManagerFilter
                    managers={managers}
                    value={reqManager}
                    onValueChange={setReqManager}
                    testId="bonus-req-manager-filter"
                  />
                  <MonthFilter
                    value={reqBonusMonth}
                    onValueChange={setReqBonusMonth}
                    testId="bonus-req-bonus-month-filter"
                    allLabel="All Bonus Months"
                  />
                  <MonthFilter
                    value={reqPayoutMonth}
                    onValueChange={setReqPayoutMonth}
                    testId="bonus-req-payout-month-filter"
                    allLabel="All Payout Months"
                  />
                  <Input
                    type="number"
                    min="2000"
                    placeholder="Payout year"
                    value={reqPayoutYear}
                    onChange={(event) => setReqPayoutYear(event.target.value)}
                    className="w-full sm:w-[140px]"
                    aria-label="Payout year"
                    data-testid="bonus-req-payout-year-filter"
                  />
                </div>
                {canApprove && selectedRequestIds.length > 0 && requestStatus === 'Pending' && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => setConfirmAction({ type: 'approve-bulk', ids: [...selectedRequestIds] })}
                      data-testid="bonus-bulk-approve"
                    >
                      <CheckCircle className="w-3.5 h-3.5 mr-1" />
                      Approve ({selectedRequestIds.length})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setConfirmAction({ type: 'reject-bulk', ids: [...selectedRequestIds] })}
                      data-testid="bonus-bulk-reject"
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1" />
                      Reject ({selectedRequestIds.length})
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    {canApprove && requestStatus === 'Pending' && (
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allPendingSelected}
                          onCheckedChange={(value) => handleToggleAllPending(!!value)}
                          aria-label="Select all pending"
                          data-testid="bonus-req-select-all"
                        />
                      </TableHead>
                    )}
                    <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Payout</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Net Bonus</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">TDS</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">By Cheque</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">By Cash</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest min-w-[140px]">Remarks</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Requested</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                    {canApprove && <TableHead className="text-xs uppercase tracking-widest">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableLoading && (
                    <TableRow>
                      <TableCell colSpan={requestColSpan} className="text-center py-8 text-muted-foreground">
                        Loading requests...
                      </TableCell>
                    </TableRow>
                  )}
                  {!tableLoading && requests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={requestColSpan} className="text-center py-8 text-muted-foreground">
                        No bonus requests found
                      </TableCell>
                    </TableRow>
                  )}
                  {!tableLoading &&
                    requests.map((req) => {
                      const id = requestIdOf(req);
                      const pendingRow = req.status === 'Pending';
                      const typeLabel =
                        req.bonusType === 'Percentage'
                          ? `${req.bonusPercentage ?? '-'}%`
                          : `Fixed ${formatCurrency(req.bonusAmount)}`;
                      const companyLabel =
                        req.companyName || companyNameById[req.company] || req.company || '';
                      return (
                        <TableRow key={id} data-testid={`bonus-req-row-${id}`}>
                          {canApprove && requestStatus === 'Pending' && (
                            <TableCell>
                              <Checkbox
                                checked={selectedRequestIds.includes(id)}
                                disabled={!pendingRow}
                                onCheckedChange={(value) => handleToggleRequest(id, !!value)}
                                aria-label={`Select ${id}`}
                                data-testid={`bonus-req-check-${id}`}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">{req.employeeName}</p>
                              <p className="text-xs text-muted-foreground font-mono">{req.employeeId}</p>
                              <p className="text-xs text-muted-foreground">
                                {[req.department, companyLabel].filter(Boolean).join(' · ')}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">{typeLabel}</TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {req.payoutMonth ? `${monthLabel(req.payoutMonth)} ${req.payoutYear || ''}`.trim() : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {formatCurrency(req.netAnnualBonus ?? req.calculatedAnnualBonus)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {formatCurrency(req.calculatedAnnualTds)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatCurrency(req.byCheque)}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatCurrency(req.byOther)}</TableCell>
                          <TableCell className="text-sm max-w-[200px]">
                            <p className="truncate" title={req.remarks || ''}>
                              {req.remarks?.trim() ? req.remarks : '-'}
                            </p>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div>
                              <p>{formatDate(req.requestedAt || req.createdAt)}</p>
                              <p className="text-xs text-muted-foreground">{req.requestedBy?.name || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusBadgeClass(req.status)}>{req.status}</Badge>
                          </TableCell>
                          {canApprove && (
                            <TableCell>
                              {pendingRow ? (
                                <div className="flex flex-wrap gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-green-600 border-green-200 hover:bg-green-50"
                                    onClick={() => setConfirmAction({ type: 'approve-one', ids: [id] })}
                                    data-testid={`bonus-approve-${id}`}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={() => setConfirmAction({ type: 'reject-one', ids: [id] })}
                                    data-testid={`bonus-reject-${id}`}
                                  >
                                    <XCircle className="w-3 h-3 mr-1" /> Reject
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">{req.reviewedBy?.name || '-'}</span>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {reqTotal} request{reqTotal === 1 ? '' : 's'}
            </p>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <Select
                value={String(reqPageSize)}
                onValueChange={(value) => {
                  setReqPageSize(Number(value));
                  setReqPage(1);
                }}
              >
                <SelectTrigger className="w-[100px]" data-testid="bonus-req-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size} / page
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={reqPage <= 1}
                onClick={() => setReqPage((page) => Math.max(1, page - 1))}
              >
                Prev
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {reqPage} / {Math.max(1, reqPages)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={reqPage >= reqPages}
                onClick={() => setReqPage((page) => Math.min(reqPages, page + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(open) => {
          if (!open && !actionLoading) setConfirmAction(null);
        }}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel={confirmAction?.type?.startsWith('approve') ? 'Approve' : 'Reject'}
        variant={confirmAction?.type?.startsWith('approve') ? 'success' : 'destructive'}
        loading={actionLoading}
        onConfirm={runConfirmAction}
        data-testid="bonus-action-confirm"
      />
    </div>
  );
};

export default BonusPage;
